import {
    Box,
    TextField,
    Typography,
    Grid,
    Autocomplete,
    CircularProgress,
    Accordion,
    Chip,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import {
    Close as CloseIcon,
    Add as AddIcon,
    Person as PersonIcon,
    CalendarMonth as CalendarIcon,
    Event as EventIcon,
    AccountCircle as AccountCircleIcon,
    DragIndicator as DragIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    TableChart as TableIcon,
    ViewModule as KanbanIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Polyline as PolylineIcon,
    ManageAccounts as ManageAccountsIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Sync as SyncIcon,
    ElectricalServices as TowerIcon,
    AccountTree as AccountTreeIcon,
    ExpandMore as ExpandMoreIcon,
    Archive as ArchiveIcon,
    Clear as ClearIcon,
    LocationOn as LocationIcon
} from '@mui/icons-material';
import { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { validatePlanId } from "../../../http/userApi";
import { marked } from 'marked';
import { DeadlineDateIcon, PersonCustomIcon } from "../TasksView";
import { statusDefs } from "../TasksViewNew";
import { selectMappedElectricity } from "../../../store/selectors/supervisionSelector";

// Настройка marked для лучшей работы со списками
marked.setOptions({
    breaks: true, // Преобразует \n в <br>
    gfm: true, // GitHub Flavored Markdown
    sanitize: false, // Разрешаем HTML теги
    smartLists: true, // Умная обработка списков
    smartypants: false // Отключаем умные кавычки

});



const TaskForm = ({
    formData,
    handleFormChange,
    formatDateForInput,
    users,
    specs,
    loadingUsers,
    setFormData,
    darkMode,
    currentUser,
    taskType,
    userRoles,
    isEditing,
    editingTask,
    getLayerName,
    getObjectName,
    objectsIds,
    objects,
    planValidation,
    setPlanValidation
}) => {
    // Получаем mappedElectricity для формирования имен объектов
    const mappedElectricity = useSelector(selectMappedElectricity);

    // Автоматически удаляем ответственного из соисполнителей при изменении ответственного
    useEffect(() => {
        if (formData.responsible && formData.coexecutors?.includes(formData.responsible)) {
            setFormData(prev => ({
                ...prev,
                coexecutors: prev.coexecutors.filter(id => id !== formData.responsible)
            }));
        }
    }, [formData.responsible]);

    // Функция валидации номера плана
    const validatePlan = useCallback(async (title, responsible_id) => {
        console.log(title, responsible_id)
        if (!objects || !title || !responsible_id) {
            setPlanValidation({
                isValidating: false,
                isValid: true,
                error: null,
                alternatives: []
            });
            return;
        }

        // Проверяем, что это плановая задача с номером (не звездочкой)
        const planMatch = title.match(/^План (\d+) /);

        if (!planMatch) {
            setPlanValidation({
                isValidating: false,
                isValid: true,
                error: null,
                alternatives: []
            });
            return;
        }

        const planId = parseInt(planMatch[1]);
        console.log(planId)
        if (isNaN(planId)) return;

        if (isEditing) {
            const defaultPlainId = parseInt(editingTask.title.match(/^План (\d+) /)[1])
            if (planId === defaultPlainId) {
                setPlanValidation({
                    isValidating: false,
                    isValid: true,
                    error: null,
                    alternatives: []
                });
                return;
            }
        }

        setPlanValidation(prev => ({ ...prev, isValidating: true }));

        try {
            const response = await validatePlanId(responsible_id, planId);
            if (response.success) {
                if (response.data.available) {
                    setPlanValidation({
                        isValidating: false,
                        isValid: true,
                        error: null,
                        alternatives: []
                    });
                } else {
                    setPlanValidation({
                        isValidating: false,
                        isValid: false,
                        error: response.data.message,
                        alternatives: response.data.alternatives || []
                    });
                }
            }
        } catch (error) {
            console.error('Ошибка валидации плана:', error);
            setPlanValidation({
                isValidating: false,
                isValid: false,
                error: 'Ошибка проверки номера плана',
                alternatives: []
            });
        }
    }, [objects]);

    // Отслеживаем изменения в названии задачи для валидации планов
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            validatePlan(formData.name, formData.responsible);
        }, 500); // Дебаунс 500мс

        return () => clearTimeout(timeoutId);
    }, [formData.responsible, formData.name, validatePlan]);

    // Создаем сводные списки материалов для всех типов объектов
    const consolidatedMaterialsByType = useMemo(() => {
        if (!objects || !specs) return {};

        const result = {};
        console.log(objectsIds, objects)
        Object.keys(objectsIds).forEach(type => {
            const materials = new Map();

            objects[type]?.forEach((obj) => {
                if (!obj.spec) return;
                let spec = null;

                if (type === 'fiders') {
                    // Особая логика для фидеров - берем спецификацию провода и вычисляем длину
                    spec = Array.isArray(obj.specs) && obj.specs.length > 0 ? obj.specs.map(s => specs[s]) : [specs[obj.spec]]

                    spec.filter(Boolean).map((_spec, i) => {
                        if (_spec?.name && obj.chains && obj.distance) {
                            const totalLength = obj.chains[i] * obj.distance;
                            const key = `${_spec.name}_fider_cable`;

                            if (materials.has(key)) {
                                const existing = materials.get(key);
                                existing.count += totalLength;
                                existing.objects.add(getObjectName(type, obj));
                            } else {
                                materials.set(key, {
                                    name: _spec.name,
                                    type_marka_docs: _spec.type_marka_docs,
                                    factory_manufacturer: _spec.factory_manufacturer,
                                    unit: 'м',
                                    count: totalLength,
                                    mass: 0, // Для проводов массу не считаем
                                    type: _spec.category,
                                    objects: new Set([getObjectName(type, obj)])
                                });
                            }
                        }
                    })
                } else {
                    if (obj.spec_complect) {
                        spec = specs[obj.spec]?.[Number(obj.spec_complect)]
                    } else {
                        spec = Object.values(specs[obj.spec])[0]
                    }
                    // Стандартная логика для остальных типов объектов
                    function complectProcessing(level, typeName, complect, parentCount = 1) {
                        complect.forEach(material => {
                            const key = `${material.name}_${material.type_marka_docs}_${material.factory_manufacturer}_${typeName}`;
                            const count = (parseFloat(material.count) * parentCount) || 0;
                            if (materials.has(key)) {
                                const existing = materials.get(key);
                                existing.count += count;
                                existing.mass += (parseFloat(material.massa_1ed_kg) * count) || 0;
                                existing.objects.add([getObjectName(type, obj), getLayerName(obj.type)]);
                            } else {
                                materials.set(key, {
                                    name: material.name,
                                    type_marka_docs: material.type_marka_docs,
                                    factory_manufacturer: material.factory_manufacturer,
                                    unit: material.unit,
                                    count: count,
                                    mass: (parseFloat(material.massa_1ed_kg) * count) || 0,
                                    type: typeName, // Добавляем тип материала из complect_filtered
                                    objects: new Set([[getObjectName(type, obj), getLayerName(obj.type)]])
                                });
                            }
                            if (material.complect) {
                                complectProcessing(level + 1, material.type, material.complect, count);
                            }
                        });

                    }
                    //Стандартная логика для остальных типов объектов
                    if (spec?.complect_filtered) {
                        spec.complect_filtered.forEach(cf => {
                            complectProcessing(0, cf.type, cf.complect);

                        });
                    }
                }
            });
            result[type] = Array.from(materials.values());
        });

        return result;
    }, [objects, getObjectName]);

    // Определяем ограничения полей для роли user
    const canEditField = (fieldName) => {
        if (userRoles.some(r => ['admin', 'project_manager'].includes(r))) return true;
        if (userRoles.includes('viewer')) return false;
        if (userRoles.includes('user')) {
            if (!isEditing) return true; // При создании задачи - все поля доступны

            // При редактировании - если пользователь создатель задачи, может редактировать все поля
            if (editingTask?.creator === currentUser.id) return true;

            // Если пользователь ответственный, может редактировать только статус (но это обрабатывается через drag&drop)
            // В форме ему доступно только описание и соисполнители
            return fieldName === 'desc' || fieldName === 'coexecutors';
        }
        return false;
    };

    const canEditResponsible = () => {
        if (userRoles.some(r => ['admin', 'project_manager'].includes(r))) return true;
        if (userRoles.includes('user') && (!isEditing || editingTask?.creator === currentUser.id)) return true;
        return false;
    };



    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: 2,
        gap: 1,
        position: 'relative',
        zIndex: 1,
        '& .MuiTextField-root': {
            '& .MuiOutlinedInput-root': {
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 1,
                '& input, & textarea': {
                    cursor: 'text !important',
                    pointerEvents: 'auto',
                    zIndex: 2,
                    color: darkMode ? '#f5f7fa' : '#333'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                },
                '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                    }
                },
                '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px',
                        borderColor: '#1976d2'
                    }
                },
                '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? '#f44336' : '#d32f2f'
                    }
                }
            },
            '& .MuiInputLabel-root': {
                color: darkMode ? '#bbb' : '#666',
                fontWeight: 500,
                '&.Mui-focused': {
                    fontWeight: 600,
                    color: '#1976d2'
                }
            },
            '& .MuiFormHelperText-root': {
                color: darkMode ? '#f44336' : '#d32f2f'
            }
        }
    }}>
        {/* Название задачи */}
        <Box>
            <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 600,
                color: darkMode ? '#f5f7fa' : '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                Название задачи *
            </Typography>
            <TextField
                fullWidth
                placeholder="Введите название задачи"
                value={formData.name}
                onChange={handleFormChange('name')}
                required
                variant="outlined"
                size="medium"
                disabled={!canEditField('name')}
                error={!formData.name.trim() ||
                    formData.name.length === 0 ||
                    (taskType === 'contour' && !formData.name.match(/^План (\*|\d+) /)) ||
                    (!taskType === 'contour' && formData.name.match(/^План (\*|\d+) /)) ||
                    (taskType === 'contour' && !planValidation.isValid)
                }
                slotProps={{
                    input: {
                        endAdornment: planValidation.isValidating && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )
                    }
                }}
                sx={{
                    '& .MuiOutlinedInput-input': {
                        p: 1,
                        cursor: 'text',
                        pointerEvents: 'auto',
                        '&::placeholder': {
                            color: darkMode ? '#999' : '#666',
                            opacity: 1
                        }
                    }
                }}
            />
            {/* Сообщения об ошибках валидации */}
            {taskType === 'contour' && !formData.name.match(/^План (\*|\d+) /) &&
                <Typography variant="body2" sx={{
                    mt: 1,
                    fontWeight: 400,
                    color: darkMode ? '#f44336' : '#d32f2f',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <WarningIcon fontSize="small" />
                    Название плановой задачи контуру должно начинаться с "План * " или "План [номер] "
                </Typography>
            }
            {!isEditing && taskType !== 'contour' && formData.name.match(/^План (\*|\d+) /) &&
                <Typography variant="body2" sx={{
                    mt: 1,
                    fontWeight: 400,
                    color: darkMode ? '#f44336' : '#d32f2f',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <WarningIcon fontSize="small" />
                    Плановая задача создается только контуру на карте
                </Typography>
            }

            {/* Ошибка валидации номера плана от сервера */}
            {taskType === 'contour' && !planValidation.isValid && planValidation.error &&
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: darkMode ? '#f44336' : '#d32f2f',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                    }}>
                        <WarningIcon fontSize="small" />
                        {planValidation.error}
                    </Typography>
                    {planValidation.alternatives.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                mb: 1
                            }}>
                                Доступные номера планов:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {planValidation.alternatives.slice(0, 5).map(alt => (
                                    <Chip
                                        key={alt}
                                        label={`План ${alt}`}
                                        size="small"
                                        clickable
                                        onClick={() => {
                                            const newName = formData.name.replace(/^План \d+ /, `План ${alt} `);
                                            setFormData(prev => ({ ...prev, name: newName }));
                                        }}
                                        sx={{
                                            bgcolor: darkMode ? '#2196f3' : '#e3f2fd',
                                            color: darkMode ? '#fff' : '#1976d2',
                                            '&:hover': {
                                                bgcolor: darkMode ? '#1976d2' : '#bbdefb'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            }
        </Box>

        {/* Описание с поддержкой Markdown */}
        <Box>
            <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 600,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                Описание (поддерживает Markdown)
            </Typography>
            <TextField
                fullWidth
                placeholder="Подробное описание задачи"
                value={formData.desc}
                onChange={handleFormChange('desc')}
                multiline
                rows={4}
                variant="outlined"
                size="medium"
                disabled={!canEditField('desc')}
                sx={{
                    '& .MuiInputBase-root': {
                        p: 0,
                    },
                    '& .MuiOutlinedInput-input': {
                        p: 1,
                        cursor: 'text',
                        pointerEvents: 'auto',
                        // fontFamily: 'monospace'
                    },
                    '& textarea::placeholder': {
                        color: darkMode ? '#999' : '#666',
                        opacity: 1
                    }
                }}
            />
            {/* Превью Markdown (простая реализация) */}
            {formData.desc.trim() && (
                <Box sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    borderRadius: 1,
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'
                }}>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontWeight: 600
                    }}>
                        Превью:
                    </Typography>
                    <Box sx={{
                        mt: 0.5,
                        fontSize: '0.875rem',
                        color: darkMode ? '#f5f7fa' : '#333',
                        '& p': {
                            margin: 0
                        },

                    }}
                        dangerouslySetInnerHTML={{
                            __html: marked(formData.desc)
                        }}
                    />
                </Box>
            )}
        </Box>

        {/* Строка с датой и ответственным */}
        <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    // color: darkMode ? '#f5f7fa' : '#333',
                    color: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    {/* <CalendarIcon sx={{ fontSize: '16px', color: '#2196f3' }} /> */}
                    <DeadlineDateIcon color={'#1976D2'} />
                    Дедлайн *
                </Typography>
                <TextField
                    fullWidth
                    type="datetime-local"
                    required
                    disabled={!canEditField('deadline')}
                    error={!formData.deadline}
                    value={formatDateForInput(formData.deadline)}
                    onChange={handleFormChange('deadline')}
                    slotProps={{
                        inputLabel: { shrink: true }
                    }}
                    variant="outlined"
                    size="medium"
                    sx={{
                        '& .MuiOutlinedInput-input': {
                            p: 1,
                            cursor: 'text',
                            pointerEvents: 'auto',
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& .MuiSvgIcon-root': {
                            color: darkMode ? '#bbb' : '#666'
                        },
                        '& .MuiIconButton-root': {
                            color: darkMode ? '#bbb' : '#666',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            }
                        },
                        '& .MuiInputAdornment-root': {
                            '& .MuiIconButton-root': {
                                color: darkMode ? '#bbb' : '#666',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                }
                            }
                        },
                        '& .MuiOutlinedInput-root': {
                            '& .MuiIconButton-root': {
                                color: darkMode ? '#bbb' : '#666',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                }
                            },
                            '& .MuiSvgIcon-root': {
                                color: darkMode ? '#bbb' : '#666'
                            }
                        },
                        // Специфичные селекторы для datetime-local
                        '& input[type="datetime-local"]': {
                            // colorScheme: darkMode ? 'dark' : 'light',
                            '&::-webkit-calendar-picker-indicator': {
                                // filter: darkMode ? 'invert(1) brightness(1.2)' : 'none',
                                color: darkMode ? '#bbb' : '#666',
                                cursor: 'pointer',
                                opacity: 1
                            }
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-fields-wrapper': {
                            background: 'transparent'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-text': {
                            color: darkMode ? '#f5f7fa' : '#333',
                            padding: '0 2px'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-month-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-day-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-year-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-hour-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-minute-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        },
                        '& input[type="datetime-local"]::-webkit-datetime-edit-ampm-field': {
                            color: darkMode ? '#f5f7fa' : '#333'
                        }
                    }}
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    // color: darkMode ? '#f5f7fa' : '#333',
                    color: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    {/* <PersonIcon sx={{ fontSize: '16px', color: '#9c27b0' }} /> */}
                    <PersonCustomIcon color={'#1976D2'} />
                    Ответственный *
                </Typography>
                <Autocomplete
                    fullWidth
                    options={users}
                    getOptionLabel={(option) => option.name || option.display_name || ''}
                    value={users.find(user => user.id === formData.responsible) || null}
                    onChange={(event, newValue) => {
                        setFormData(prev => ({
                            ...prev,
                            responsible: newValue.id
                        }));
                    }}
                    disabled={loadingUsers || !canEditResponsible()}
                    loading={loadingUsers}
                    noOptionsText="Нет пользователей в проекте"
                    loadingText="Загрузка..."
                    placeholder="Начните вводить имя..."
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={!formData.responsible}
                            variant="outlined"
                            size="medium"

                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    width: '300px',
                                    py: 0,
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    zIndex: 1,
                                    '& input': {
                                        p: 1,
                                        cursor: 'text',
                                        pointerEvents: 'auto',
                                        color: darkMode ? '#f5f7fa' : '#333'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                    },
                                    '&:hover': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        }
                                    },
                                    '&.Mui-focused': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '2px',
                                            borderColor: '#1976d2'
                                        }
                                    },
                                    '&.Mui-error': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? '#f44336' : '#d32f2f'
                                        }
                                    }
                                },
                                '& .MuiSvgIcon-root': {
                                    color: darkMode ? '#bbb' : '#666'
                                },
                                '& .MuiIconButton-root': {
                                    color: darkMode ? '#bbb' : '#666',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                    }
                                }
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{
                            color: darkMode ? '#f5f7fa' : '#333',
                            whiteSpace: 'nowrap',
                            minWidth: 'max-content',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 'max-content'
                            }}>
                                <Typography sx={{
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {option.name || option.display_name}
                                </Typography>
                                {/* {option.display_name && option.username !== option.display_name && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: darkMode ? '#999' : 'text.secondary'
                                                }}
                                            >
                                        {option.username}
                                            </Typography>
                                        )} */}
                            </Box>
                        </Box>
                    )}
                    sx={{
                        pointerEvents: 'auto',
                        '& .MuiAutocomplete-paper': {
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        },
                        '& .MuiAutocomplete-option': {
                            whiteSpace: 'nowrap',
                            overflow: 'visible'
                        }
                    }}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    minWidth: 'max-content'
                                },
                                '& .MuiAutocomplete-option': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    minWidth: 'max-content'
                                }
                            }
                        }
                    }}
                />
            </Grid>
        </Grid>

        {/* Соисполнители */}
        <Box>
            <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 600,
                color: '#9c27b0',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <SupervisorAccountIcon sx={{ fontSize: '16px' }} />
                Соисполнители
            </Typography>
            <Autocomplete
                multiple
                fullWidth
                disableCloseOnSelect
                options={users.filter(user => user.id !== formData.responsible)}
                getOptionLabel={(option) => option.name || option.display_name || ''}
                value={users.filter(user => formData.coexecutors?.includes(user.id)) || []}
                onChange={(event, newValue) => {
                    setFormData(prev => ({
                        ...prev,
                        coexecutors: newValue.map(n => n.id)
                    }));
                }}
                disabled={loadingUsers || !canEditField('coexecutors') || !formData.responsible}
                loading={loadingUsers}
                noOptionsText={!formData.responsible ? "Сначала выберите ответственного" : "Нет пользователей в проекте"}
                loadingText="Загрузка..."
                placeholder={!formData.responsible ? "Сначала выберите ответственного" : "Выберите соисполнителей..."}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        size="medium"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                borderRadius: 2,
                                py: 0,
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                zIndex: 1,
                                '& input': {
                                    p: 1,
                                    cursor: !formData.responsible ? 'not-allowed' : 'text',
                                    pointerEvents: 'auto',
                                    color: darkMode ? '#f5f7fa' : '#333'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                },
                                '&:hover': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                    }
                                },
                                '&.Mui-focused': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderWidth: '2px',
                                        borderColor: '#9c27b0'
                                    }
                                }
                            },
                            '& .MuiSvgIcon-root': {
                                color: darkMode ? '#bbb' : '#666'
                            },
                            '& .MuiIconButton-root': {
                                color: darkMode ? '#bbb' : '#666',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                }
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{
                        color: darkMode ? '#f5f7fa' : '#333',
                        whiteSpace: 'nowrap',
                        minWidth: 'max-content',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                        }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 'max-content'
                        }}>
                            <Typography sx={{
                                color: darkMode ? '#f5f7fa' : '#333',
                                fontWeight: 500,
                                whiteSpace: 'nowrap'
                            }}>
                                {option.name || option.display_name}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.name || option.display_name}
                            size="small"
                            sx={{
                                bgcolor: darkMode ? '#9c27b0' : '#e1bee7',
                                color: darkMode ? '#fff' : '#4a148c',
                                '& .MuiChip-deleteIcon': {
                                    color: darkMode ? '#fff' : '#4a148c',
                                    '&:hover': {
                                        color: darkMode ? '#ffcdd2' : '#7b1fa2'
                                    }
                                }
                            }}
                        />
                    ))
                }
                sx={{
                    pointerEvents: 'auto',
                    '& .MuiAutocomplete-paper': {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                    },
                    '& .MuiAutocomplete-option': {
                        whiteSpace: 'nowrap',
                        overflow: 'visible'
                    }
                }}
                slotProps={{
                    popper: {
                        sx: {
                            zIndex: 13000,
                            '& .MuiAutocomplete-paper': {
                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                minWidth: 'max-content'
                            },
                            '& .MuiAutocomplete-option': {
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                                minWidth: 'max-content'
                            }
                        }
                    }
                }}
            />
            {/* Подсказка когда поле неактивно */}
            {!formData.responsible && (
                <Typography variant="caption" sx={{
                    mt: 1,
                    color: darkMode ? '#999' : '#666',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                }}>
                    <WarningIcon fontSize="small" />
                    Сначала выберите ответственного для активации поля соисполнителей
                </Typography>
            )}
        </Box>

        {/* Информация о создателе (только для отображения) */}
        <Box sx={{
            mt: 1,
            p: 1,
            bgcolor: darkMode ? 'rgba(124, 179, 66, 0.15)' : 'rgba(124, 179, 66, 0.08)',
            border: darkMode ? '1px solid rgba(124, 179, 66, 0.4)' : '1px solid rgba(124, 179, 66, 0.3)',
            borderRadius: 2,
            gap: 2,
            display: 'flex',
            flexDirection: 'row'
        }}>
            <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                // color: darkMode ? '#ffb74d' : '#ff9800',
                color: '#7CB342',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                {/* <AccountCircleIcon sx={{ fontSize: '16px' }} /> */}
                <PersonCustomIcon color={'#7CB342'} />
                Автор задачи:
            </Typography>
            <Typography variant="body2" sx={{
                // color: darkMode ? '#ffe0b2' : '#e65100',
                color: '#7CB342',
                fontStyle: 'italic'
            }}>
                {isEditing ? (users.find(u => u.id === formData.creator)?.name) : currentUser.display_name}
            </Typography>
        </Box>

        {/* Информация о найденных объектах (если есть) */}
        {(() => {
            // Определяем объекты для отображения: либо из новой задачи по контуру, либо из редактируемой задачи
            const objectsToShow = objectsIds || null;
            console.log(objectsToShow)
            if (!objectsToShow || !Object.keys(objectsToShow).some(type => objectsToShow[type]?.length > 0)) {
                return null;
            }

            return (
                <Box sx={{
                    p: 2,
                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.05)',
                    border: darkMode ? '1px solid rgba(156, 39, 176, 0.4)' : '1px solid rgba(156, 39, 176, 0.3)',
                    borderRadius: 2,
                    mt: 1
                }}>
                    <Typography variant="subtitle2" sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: darkMode ? '#ce93d8' : '#9c27b0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        {/* <CheckCircleIcon sx={{ fontSize: '18px' }} /> */}
                        <AccountTreeIcon sx={{
                            fontSize: '18px',
                            color: darkMode ? '#ce93d8' : '#9c27b0'
                        }} />
                        Материалы
                    </Typography>
                    {Object.keys(objectsToShow).map((type, index) => {
                        if (!objectsToShow[type]?.length) return null;

                        // Получаем предвычисленный сводный список материалов для типа
                        const consolidatedMaterials = consolidatedMaterialsByType[type] || [];
                        console.log(consolidatedMaterials)
                        return (
                            <Accordion
                                key={index}
                                sx={{
                                    mb: 1,
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    borderRadius: 1,
                                    '&:before': {
                                        display: 'none',
                                    },
                                    boxShadow: darkMode
                                        ? '0 1px 3px rgba(0,0,0,0.3)'
                                        : '0 1px 3px rgba(0,0,0,0.1)',
                                    '& .MuiButtonBase-root': {
                                        minHeight: 'auto',
                                    }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: darkMode ? '#ce93d8' : '#9c27b0' }} />}
                                    sx={{
                                        '& .MuiAccordionSummary-content': {
                                            my: 0.5,
                                            alignItems: 'center'
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{
                                        fontWeight: 600,
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.875rem'
                                    }}>
                                        {getLayerName(type)}: {type === 'fiders' ? `${consolidatedMaterials.reduce((sum, arr) => sum + (arr?.count || 0), 0)} м` : `${objectsToShow[type].length} шт`}
                                    </Typography>
                                    {consolidatedMaterials.length > 0 && (
                                        <Chip
                                            label={`${consolidatedMaterials.length} наим.`}
                                            size="small"
                                            sx={{
                                                ml: 2,
                                                bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                                color: darkMode ? '#ce93d8' : '#9c27b0',
                                                fontSize: '0.7rem',
                                                height: '20px'
                                            }}
                                        />
                                    )}
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {consolidatedMaterials.length > 0 ? (
                                        <TableContainer component={Paper} sx={{
                                            bgcolor: darkMode ? '#1e1e1e' : '#f9f9f9',
                                            borderRadius: 1,
                                            maxHeight: 400,
                                            '& .MuiTableCell-root': {
                                                borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                                                color: darkMode ? '#f5f7fa' : '#333',
                                                fontSize: '0.75rem',
                                                padding: '6px 8px'
                                            }
                                        }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Наименование
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Тип, марка
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            textAlign: 'center',
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Завод изготовитель
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            textAlign: 'center',
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Количество
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            textAlign: 'center',
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Масса, кг
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            bgcolor: darkMode ? '#333' : '#f5f5f5',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}>
                                                            Объекты
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(() => {
                                                        // Группируем материалы по типам
                                                        const groupedMaterials = new Map();

                                                        consolidatedMaterials.forEach((material) => {
                                                            const materialType = material.type || material.category || 'Прочие';
                                                            if (!groupedMaterials.has(materialType)) {
                                                                groupedMaterials.set(materialType, []);
                                                            }
                                                            groupedMaterials.get(materialType).push(material);
                                                        });

                                                        // Создаем массив строк для отображения
                                                        const rows = [];
                                                        let rowIndex = 0;

                                                        groupedMaterials.forEach((materials, type) => {
                                                            // Добавляем заголовок группы
                                                            rows.push(
                                                                <TableRow key={`header_${rowIndex}`} sx={{
                                                                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'
                                                                }}>
                                                                    <TableCell colSpan={6} sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: '0.8rem',
                                                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {type}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                            rowIndex++;

                                                            // Добавляем материалы этого типа
                                                            materials.forEach((material, matIndex) => {
                                                                rows.push(
                                                                    <TableRow key={`material_${rowIndex}_${matIndex}`} sx={{
                                                                        '&:hover': {
                                                                            bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                                                                        }
                                                                    }}>
                                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                                            {material.name}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {material.type_marka_docs}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {material.factory_manufacturer}
                                                                        </TableCell>
                                                                        <TableCell sx={{ textAlign: 'center' }}>
                                                                            {material.count.toString().replace('.', ',')} {material.unit}
                                                                        </TableCell>
                                                                        <TableCell sx={{ textAlign: 'center' }}>
                                                                            {material.mass > 0 ? material.mass.toFixed(2).replace('.', ',') : '-'}
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: '0.65rem' }}>
                                                                            {Array.from(material.objects).join(', ')}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                                rowIndex++;
                                                            });
                                                        });

                                                        return rows;
                                                    })()}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Typography variant="body2" sx={{
                                            color: darkMode ? '#bbb' : '#666',
                                            fontStyle: 'italic',
                                            textAlign: 'center',
                                            py: 2
                                        }}>
                                            Нет спецификаций для объектов этого типа
                                        </Typography>
                                    )}

                                    <Typography variant="caption" sx={{
                                        color: darkMode ? '#999' : '#666',
                                        display: 'block',
                                        mt: 1,
                                        fontSize: '0.7rem'
                                    }}>
                                        Объекты: {[... new Set(objects[type]?.map(obj => getObjectName(type, obj)))].join(', ')}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            );
        })()}
    </Box>
};


const TaskCreateAndSaveDialog = ({
    formData,
    task,
    users,
    saveCallback,
    cancelCallabck,
    loadingUsers,
    darkMode,
    isMobile,
    currentUser,
    taskType,
    isEditing,
    getLayerName,
    getObjectName,
    objectsIds,
    objects,
    specs
}) => {
    const [localFormData, setLocalFormData] = useState(formData);
    const [planValidation, setPlanValidation] = useState({
        isValidating: false,
        isValid: true,
        error: null,
        alternatives: []
    });

    // Функция для форматирования даты в формат yyyy-MM-ddTHH:mm
    const formatDateForInput = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            // Получаем локальную дату и время в формате ISO
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
            return '';
        }
    }, []);

    // Обработчик изменения полей формы
    const handleFormChange = useCallback((field) => (event) => {
        setLocalFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    }, []);

    return <Dialog
        open={true}
        // onClose={() => {
        //     setCreateDialogOpen(false);
        //     resetForm();
        // }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: 5100 }}
        slotProps={{
            paper: {
                sx: {
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    color: darkMode ? '#f5f7fa' : '#333',
                    borderRadius: isMobile ? 0 : 3,
                    boxShadow: darkMode
                        ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                        : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    animation: 'slideIn 0.3s ease-out',
                    position: 'relative',
                    zIndex: 1301,
                    '@keyframes slideIn': {
                        from: {
                            opacity: 0,
                            transform: 'scale(0.95)',
                        },
                        to: {
                            opacity: 1,
                            transform: 'scale(1)',
                        },
                    },
                }
            }
        }}
    >
        <DialogTitle sx={{
            bgcolor: darkMode ? '#2b2b2b' : '#fff',
            borderBottom: darkMode
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(0, 0, 0, 0.12)',
            py: 1,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: isEditing ? 'rgba(25, 118, 210, 0.1)' : 'rgba(124, 179, 66, 0.1)',
                    border: isEditing ? '2px solid #1976d2' : '2px solid #7cb342'
                }}>
                    {isEditing ?
                        <EditIcon sx={{
                            fontSize: '24px',
                            color: '#1976d2'
                        }} /> :
                        <AddIcon sx={{
                            fontSize: '24px',
                            color: '#7cb342'
                        }} />}
                </Box>
                <Box>
                    <Typography variant="h6" sx={{
                        fontWeight: 700,
                        mb: 0.3,
                        color: darkMode ? '#f5f7fa' : '#333'
                    }}>
                        {isEditing ? 'Редактировать задачу' : 'Создание новой задачи'}
                    </Typography>
                    {/* <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Создатель: {currentUser.display_name}
                            </Typography> */}
                </Box>
            </Box>

            <IconButton
                onClick={cancelCallabck}
                size="large"
                sx={{
                    color: darkMode ? '#f5f7fa' : '#666',
                    '&:hover': {
                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                        color: '#f44336'
                    }
                }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>

        <DialogContent sx={{
            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
            p: 3,
            position: 'relative',
            zIndex: 1302
        }}>
            <TaskForm
                formData={localFormData}
                handleFormChange={handleFormChange}
                formatDateForInput={formatDateForInput}
                users={users}
                specs={specs}
                taskType={taskType}
                loadingUsers={loadingUsers}
                setFormData={setLocalFormData}
                darkMode={darkMode}
                currentUser={currentUser}
                // newTaskObjects={newTaskObjects}
                userRoles={currentUser.roles}
                isEditing={isEditing}
                editingTask={task}
                getLayerName={getLayerName}
                getObjectName={getObjectName}
                // taskType={'new'}
                // task={null}
                objectsIds={objectsIds}
                objects={objects}
                planValidation={planValidation}
                setPlanValidation={setPlanValidation}
            />
        </DialogContent>

        <DialogActions sx={{
            bgcolor: darkMode ? '#2b2b2b' : '#fff',
            borderTop: darkMode
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(0, 0, 0, 0.12)',
            py: 1.5,
            px: 3,
            gap: 2,
            justifyContent: 'flex-end'
        }}>
            <Button
                onClick={cancelCallabck}
                variant="outlined"
                size="large"
                sx={{
                    color: darkMode ? '#bbb' : '#666',
                    borderColor: darkMode ? '#555' : '#ddd',
                    px: 1,
                    py: 0.3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    '&:hover': {
                        borderColor: darkMode ? '#777' : '#bbb',
                        bgcolor: 'transparent'
                    }
                }}
            >
                Отмена
            </Button>
            <Button
                onClick={() => saveCallback(localFormData)}
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                disabled={!localFormData.name.trim() ||
                    !localFormData.responsible ||
                    !localFormData.deadline ||
                    (taskType === 'contour' && !localFormData.name.match(/^План (\*|\d+) /)) ||
                    (taskType !== 'contour' && localFormData.name.match(/^План (\*|\d+) /)) ||
                    (objects && planValidation.isValidating) ||
                    (objects && !planValidation.isValid)
                }
                sx={{
                    bgcolor: isEditing ? '#1976d2' : '#7cb342',
                    color: 'white',
                    px: 1,
                    py: 0.3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    '&:hover': {
                        bgcolor: isEditing ? '#1565c0' : '#689f38',
                        transform: 'translateY(-2px)',
                        boxShadow: isEditing ? '0 4px 12px rgba(25, 118, 210, 0.4)' : '0 4px 12px rgba(124, 179, 66, 0.4)'
                    },
                    '&:disabled': {
                        bgcolor: darkMode ? '#444' : '#ddd',
                        color: darkMode ? '#666' : '#999'
                    }
                }}
            >
                {isEditing ? 'Сохранить изменения' : 'Создать задачу'}
            </Button>
        </DialogActions>
    </Dialog >
}

export default TaskCreateAndSaveDialog; 